import { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty, fmtDate } from "@/components/ui-kit";
import { ApprovalBadge } from "@/components/ApprovalBadge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Check, X, ShieldCheck, Baby, ClipboardList, TrendingUp, BookOpenText, Loader2, CheckCheck } from "lucide-react";

const KIND = { student_update: ["Daily activity", Baby, "/student-updates"], daily_update: ["Daily update", ClipboardList, "/updates"],
  progress: ["Progress report", TrendingUp, "/progress"], diary: ["Class diary", BookOpenText, "/diary"] };
const REVIEWERS = ["coordinator", "principal", "center_head"];

function Decide({ it, onDone }) {
  const [reason, setReason] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [busy, setBusy] = useState(false);
  async function go(decision) {
    if (decision === "reject" && !reason.trim()) { setRejecting(true); return; }
    setBusy(true);
    try {
      if (it.kind === "diary") await api.post(`/diaries/${it.id}/review`, { status: decision === "approve" ? "approved" : "rejected", remark: reason });
      else await api.post(`/approvals/${it.kind}/${it.id}`, { decision, reason });
      toast.success(decision === "reject" ? "Sent back to teacher" : it.approval === "pending_coordinator" ? "Approved — sent to Principal" : "Approved & sent to parents");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }
  return (
    <div className="mt-3 space-y-2">
      <div className={`grid transition-all duration-300 ${rejecting ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
        <div className="overflow-hidden"><Textarea data-testid={`apv-reason-${it.id}`} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for sending back (the teacher will see this)" /></div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" disabled={busy} data-testid={`apv-approve-${it.id}`} onClick={() => go("approve")} className="bg-emerald-600 hover:bg-emerald-700 rounded-full">{busy ? <Loader2 size={14} className="animate-spin mr-1" /> : <Check size={14} className="mr-1" />}Approve</Button>
        <Button size="sm" variant="outline" disabled={busy} data-testid={`apv-reject-${it.id}`} onClick={() => go("reject")} className="text-rose-600 rounded-full"><X size={14} className="mr-1" />{rejecting ? "Confirm reject" : "Reject"}</Button>
        {rejecting && <Button size="sm" variant="ghost" onClick={() => { setRejecting(false); setReason(""); }} data-testid={`apv-cancel-${it.id}`}>Cancel</Button>}
      </div>
    </div>
  );
}

function Card({ it, canAct, onDone, i }) {
  const [label, Icon] = KIND[it.kind];
  return (
    <div data-testid={`apv-card-${it.id}`} style={{ animationDelay: `${Math.min(i, 10) * 40}ms` }}
      className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 hover:shadow-md transition-shadow animate-in fade-in slide-in-from-bottom-2 duration-300 fill-mode-both">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center shrink-0"><Icon size={18} /></div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] uppercase tracking-wide font-semibold text-slate-400">{label}</span>
            <ApprovalBadge status={it.approval} testid={`apv-status-${it.id}`} />
          </div>
          <div className="font-display font-bold text-slate-900 mt-0.5 truncate">{it.title}</div>
          <div className="text-xs text-slate-500">{it.class_name}{it.date ? ` · ${fmtDate(it.date)}` : ""} · by {it.teacher_name}</div>
          {it.lines.length > 0 && <ul className="mt-2 text-sm text-slate-600 space-y-0.5">{it.lines.map((l, j) => <li key={j} className="line-clamp-2">{l}</li>)}</ul>}
          {it.approval === "rejected" && it.review_reason && <div data-testid={`apv-reason-shown-${it.id}`} className="mt-2 text-xs rounded-lg bg-rose-50 text-rose-700 px-3 py-2"><span className="font-semibold">Reason:</span> {it.review_reason}</div>}
          {canAct && <Decide it={it} onDone={onDone} />}
        </div>
      </div>
    </div>
  );
}

function ApproveAll({ items, onDone }) {
  const [busy, setBusy] = useState("");
  const groups = Object.values(items.filter((it) => it.kind === "student_update").reduce((g, it) => {
    const k = `${it.class_name}|${it.date}`; (g[k] = g[k] || { k, class_name: it.class_name, date: it.date, ids: [] }).ids.push(it.id); return g;
  }, {})).filter((g) => g.ids.length > 1);
  if (!groups.length) return null;
  async function go(g) {
    if (!window.confirm(`Approve all ${g.ids.length} daily activities for ${g.class_name}?`)) return;
    setBusy(g.k);
    try {
      const { data } = await api.post("/approvals-bulk", { ids: g.ids });
      toast.success(`${data.approved} approved${data.skipped ? ` · ${data.skipped} skipped` : ""}`); onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  }
  return (
    <div data-testid="apv-approve-all" className="mb-4 grid sm:grid-cols-2 gap-2">
      {groups.map((g) => (
        <div key={g.k} className="flex items-center gap-3 rounded-2xl bg-emerald-50/70 border border-emerald-100 px-4 py-3 animate-in fade-in duration-300">
          <Baby size={18} className="text-emerald-600 shrink-0" />
          <div className="flex-1 min-w-0"><div className="font-semibold text-slate-900 text-sm truncate">{g.class_name}</div><div className="text-xs text-slate-500">{fmtDate(g.date)} · {g.ids.length} daily activities</div></div>
          <Button size="sm" disabled={!!busy} data-testid={`apv-approve-all-${g.k.replace(/\W+/g, "-")}`} onClick={() => go(g)} className="bg-emerald-600 hover:bg-emerald-700 rounded-full">
            {busy === g.k ? <Loader2 size={14} className="animate-spin mr-1" /> : <CheckCheck size={14} className="mr-1" />}Approve all</Button>
        </div>
      ))}
    </div>
  );
}

export default function ApprovalsPage() {
  const { user } = useAuth();
  const isTeacher = user?.role === "teacher";
  const reviewer = REVIEWERS.includes(user?.role);
  const [tab, setTab] = useState(isTeacher ? "mine" : "queue");
  const [kind, setKind] = useState("");
  const [items, setItems] = useState(null);
  const load = () => api.get("/approvals", { params: { tab, kind: kind || undefined } }).then((r) => setItems(r.data)).catch((e) => { toast.error(formatApiError(e)); setItems([]); });
  useEffect(() => { setItems(null); load(); }, [tab, kind]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <AppShell>
      <PageHeader eyebrow="Review" title={isTeacher ? "My Submissions" : "Approvals"}
        subtitle={isTeacher ? "Track what you sent. Anything sent back shows the reason — fix it and save again to resubmit." : "Teacher → Coordinator → Principal / Center Head → Parents. Only your branch is shown."} />
      <div className="flex flex-wrap items-center gap-3 mb-5">
        {!isTeacher && <Tabs value={tab} onValueChange={setTab}><TabsList className="bg-violet-100">
          <TabsTrigger value="queue" data-testid="apv-tab-queue">{reviewer ? "Waiting for me" : "Pending"}</TabsTrigger>
          <TabsTrigger value="done" data-testid="apv-tab-done">Approved & rejected</TabsTrigger>
        </TabsList></Tabs>}
        <div className="flex flex-wrap gap-1.5">
          {[["", "All"], ...Object.entries(KIND).map(([k, v]) => [k, v[0]])].map(([k, l]) => (
            <button key={k || "all"} data-testid={`apv-kind-${k || "all"}`} onClick={() => setKind(k)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${kind === k ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-600 border-slate-200 hover:border-violet-300"}`}>{l}</button>
          ))}
        </div>
      </div>
      {items === null ? <Loader2 className="animate-spin text-violet-500" /> : items.length === 0
        ? <Empty testid="apv-empty" icon={ShieldCheck} text={tab === "queue" ? "Nothing waiting — all caught up." : "Nothing here yet."} />
        : <>{reviewer && tab === "queue" && <ApproveAll items={items} onDone={load} />}<div className="grid lg:grid-cols-2 gap-3">{items.map((it, i) => <Card key={`${it.kind}-${it.id}`} i={i} it={it} onDone={load} canAct={reviewer && tab === "queue"} />)}</div></>}
    </AppShell>
  );
}
